import type { ColorSpec } from './color.js';
import type { Fill, Line } from './style.js';
import type { Centipoint, Emu } from './units.js';

export type TextAlign = 'l' | 'ctr' | 'r' | 'just' | 'dist' | 'thaiDist' | 'justLow';
export type TextAnchor = 't' | 'ctr' | 'b' | 'just' | 'dist';

/** Line spacing is either a percentage of the line height or an absolute size. */
export type LineSpacing =
  | { readonly kind: 'percent'; readonly value: number }
  | { readonly kind: 'points'; readonly value: number };

export type Bullet =
  | { readonly kind: 'none' }
  | {
      readonly kind: 'char';
      readonly char: string;
      readonly font?: string;
      /** Bullet size as a percentage of the run size. */
      readonly sizePercent?: number;
      readonly color?: ColorSpec;
    }
  | {
      readonly kind: 'autoNum';
      readonly type: string;
      readonly startAt: number;
      readonly font?: string;
      readonly sizePercent?: number;
      readonly color?: ColorSpec;
    }
  | { readonly kind: 'blip'; readonly mediaId: string; readonly sizePercent?: number };

export interface ParagraphProperties {
  readonly level: number;
  readonly align?: TextAlign;
  readonly indent?: Emu;
  readonly marginLeft?: Emu;
  readonly marginRight?: Emu;
  readonly lineSpacing?: LineSpacing;
  readonly spaceBefore?: LineSpacing;
  readonly spaceAfter?: LineSpacing;
  readonly bullet?: Bullet;
  readonly rtl?: boolean;
  readonly defaultTabSize?: Emu;
}

export type UnderlineStyle =
  | 'none'
  | 'sng'
  | 'dbl'
  | 'heavy'
  | 'dotted'
  | 'dash'
  | 'dashLong'
  | 'dotDash'
  | 'wavy'
  | 'wavyDbl';

export interface RunProperties {
  /** Hundredths of a point. `1800` is 18pt. */
  readonly size?: Centipoint;
  readonly bold?: boolean;
  readonly italic?: boolean;
  readonly underline?: UnderlineStyle;
  readonly strike?: 'noStrike' | 'sngStrike' | 'dblStrike';
  readonly caps?: 'none' | 'small' | 'all';
  /** Character spacing in hundredths of a point. */
  readonly spacing?: Centipoint;
  /** Super/subscript as a percentage; negative is subscript. */
  readonly baseline?: number;
  readonly fill?: Fill;
  readonly outline?: Line;
  readonly highlight?: ColorSpec;
  readonly latinFont?: string;
  readonly eastAsianFont?: string;
  readonly complexScriptFont?: string;
  readonly symbolFont?: string;
  readonly language?: string;
  readonly hyperlink?: Hyperlink;
}

export type Hyperlink =
  | { readonly kind: 'external'; readonly url: string; readonly tooltip?: string }
  | { readonly kind: 'slide'; readonly slideId: string; readonly tooltip?: string }
  | { readonly kind: 'action'; readonly verb: PresentationAction; readonly tooltip?: string };

export type PresentationAction =
  | 'nextslide'
  | 'previousslide'
  | 'firstslide'
  | 'lastslide'
  | 'lastslideviewed'
  | 'endshow';

export type TextRun =
  | { readonly kind: 'text'; readonly text: string; readonly props: RunProperties }
  | { readonly kind: 'break'; readonly props: RunProperties }
  | {
      readonly kind: 'field';
      readonly fieldType: string;
      readonly text: string;
      readonly props: RunProperties;
    };

export interface Paragraph {
  readonly props: ParagraphProperties;
  readonly runs: readonly TextRun[];
  /** `a:endParaRPr` — carries formatting for an empty paragraph's caret. */
  readonly endProps?: RunProperties;
}

export type AutofitMode =
  | { readonly kind: 'none' }
  | { readonly kind: 'shape' }
  | {
      readonly kind: 'normal';
      /** 0-1. PowerPoint bakes the computed shrink into the file. */
      readonly fontScale: number;
      readonly lineSpaceReduction: number;
    };

export interface BodyProperties {
  readonly anchor: TextAnchor;
  readonly anchorCenter: boolean;
  readonly wrap: 'square' | 'none';
  readonly insetLeft: Emu;
  readonly insetTop: Emu;
  readonly insetRight: Emu;
  readonly insetBottom: Emu;
  readonly autofit: AutofitMode;
  readonly columnCount: number;
  readonly columnSpacing: Emu;
  readonly vertical: 'horz' | 'vert' | 'vert270' | 'wordArtVert' | 'eaVert' | 'mongolianVert';
  readonly rotation?: number;
}

export interface TextBody {
  readonly bodyProps: BodyProperties;
  readonly paragraphs: readonly Paragraph[];
}

export const DEFAULT_BODY_PROPERTIES: BodyProperties = {
  anchor: 't',
  anchorCenter: false,
  wrap: 'square',
  insetLeft: 91440,
  insetTop: 45720,
  insetRight: 91440,
  insetBottom: 45720,
  autofit: { kind: 'none' },
  columnCount: 1,
  columnSpacing: 0,
  vertical: 'horz',
};

/** Flattens a text body to plain text. Used for TTS input, alt text and search. */
export function textBodyToPlainText(body: TextBody): string {
  return body.paragraphs
    .map((p) =>
      p.runs
        .map((r) => (r.kind === 'break' ? '\n' : r.text))
        .join('')
        .trimEnd(),
    )
    .join('\n');
}
